import { Modal } from 'react-bootstrap';
import React, { useState, useEffect } from 'react';
import { Button } from '@app/components';
import Select from "react-select";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { moveStudentWorkAhead } from '@app/services/student';

const MoveWorkAheadModal = (props) => {
    const { show, handleClose, student, subjects, subjectId, weeks, isLoading, globalWeekCounter, studentId, onSuccess } = props;

    const [selectedWeekForMove, setSelectedWeekForMove] = useState(null);
    const [numberOfWeeksToMove, setNumberOfWeeksToMove] = useState('');
    const [weekSelectError, setWeekSelectError] = useState('');
    const [weeksToMoveError, setWeeksToMoveError] = useState('');
    const [isMoveWorkLoading, setMoveWorkLoading] = useState(false);

    useEffect(() => {
        if (!show) {
            // Reset form when modal closes
            setSelectedWeekForMove(null);
            setNumberOfWeeksToMove('');
            setWeekSelectError('');
            setWeeksToMoveError('');
        }
    }, [show]);

    const handleMoveWorkSubmit = () => {
        let hasError = false;

        // Validate week selection
        if (!selectedWeekForMove) {
            setWeekSelectError('Please select a week');
            hasError = true;
        } else {
            setWeekSelectError('');
        }

        // Validate number of weeks
        if (!numberOfWeeksToMove || numberOfWeeksToMove.trim() === '') {
            setWeeksToMoveError('Please enter number of weeks to move');
            hasError = true;
        } else {
            const weeksToMove = parseInt(numberOfWeeksToMove);
            if (isNaN(weeksToMove) || weeksToMove < 1) {
                setWeeksToMoveError('Please enter a valid number of weeks (minimum 1)');
                hasError = true;
            } else {
                setWeeksToMoveError('');
            }
        }

        if (hasError) {
            return;
        }

        const weeksToMove = parseInt(numberOfWeeksToMove);

        confirmAlert({
            title: 'Confirm Move Work Ahead',
            message: `Are you sure you want to move work ahead by ${weeksToMove} week(s)?`,
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => moveWorkAhead(weeksToMove)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
    };

    const moveWorkAhead = async (weeksToMove) => {
        try {
            setMoveWorkLoading(true);
            handleClose();

            const payload = {
                student_id: parseInt(studentId),
                subject_id: subjectId,
                week_number: selectedWeekForMove.id + 1,
                number_of_weeks: weeksToMove
            };

            const resp = await moveStudentWorkAhead(payload);
            
            if (resp.success === true) {
                toast.success('Work moved ahead successfully');
                onSuccess();
            } else {
                toast.error(resp?.errors?.[0]?.msg || 'Failed to move work ahead');
            }
            setMoveWorkLoading(false);
        } catch (error: any) {
            setMoveWorkLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Move Work Ahead</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3" style={{paddingBottom: '15px', borderBottom: '1px solid #e9ecef'}}>
                    <div className="row">
                        <div className="col-6">
                            <div style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>Student Name</div>
                            <div style={{fontSize: '15px'}}>{student?.first_name} {student?.last_name}</div>
                        </div>
                        <div className="col-6">
                            <div style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>Subject</div>
                            <div style={{fontSize: '15px'}}>{subjects.find(s => s.id == subjectId)?.name || 'Mix and Match'}</div>
                        </div>
                    </div>
                </div>
                <div className="form-group" style={{marginBottom: '20px'}}>
                    <label htmlFor="weekSelect" style={{marginBottom: '8px', display: 'block'}}>Select Week <span className="text-danger">*</span></label>
                    <Select
                        name="weekForMove"
                        placeholder="Select week"
                        options={weeks}
                        getOptionValue={(option)=>`${option['id']}`}
                        getOptionLabel={(option)=>`${option['name']}`}
                        onChange={ (e) => {
                            setSelectedWeekForMove(e);
                            setWeekSelectError('');
                        }}
                        value={selectedWeekForMove}
                        isLoading={isLoading}
                        isClearable={true}
                    />
                    {weekSelectError && <div className='text-danger' style={{fontSize: '14px', marginTop: '5px'}}>{weekSelectError}</div>}
                </div>
                <div className="form-group" style={{marginBottom: '20px'}}>
                    <label htmlFor="numberOfWeeks" style={{marginBottom: '8px', display: 'block'}}>Number of Weeks to Move <span className="text-danger">*</span></label>
                    <input
                        type="number"
                        className="form-control"
                        id="numberOfWeeks"
                        min="1"
                        value={numberOfWeeksToMove}
                        onChange={(e) => {
                            setNumberOfWeeksToMove(e.target.value);
                            setWeeksToMoveError('');
                        }}
                        placeholder="1"
                    />
                    {weeksToMoveError && <div className='text-danger' style={{fontSize: '14px', marginTop: '5px'}}>{weeksToMoveError}</div>}
                    <small className="form-text text-muted" style={{marginTop: '5px'}}>How many weeks to shift forward (e.g., 1 week)</small>
                </div>
                <div className="alert alert-info" style={{backgroundColor: '#5bc0de', color: '#fff', border: 'none', borderRadius: '4px', padding: '15px', marginTop: '0', marginBottom: '0'}}>
                    <i className="fas fa-info-circle" style={{marginRight: '10px'}}></i>
                    This will move all assignments from the selected week onwards by the specified number of weeks. Completed weeks will not be affected.
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button className="btn btn-default" onClick={handleClose} disabled={isMoveWorkLoading}>
                    Cancel
                </Button>
                <Button className="btn btn-warning" onClick={handleMoveWorkSubmit} isLoading={isMoveWorkLoading}>
                    Move Work Ahead
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MoveWorkAheadModal;
